import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ErrorMessages } from '../../interfaces/errorMesagges.interface';
import { CommonModule } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DatePickerHeader } from './helpers/header/date-picker-header';
import type { MaskitoOptions } from '@maskito/core';
import dateMask from './helpers/mask';
import { FORMATS } from './helpers/formats';
import { CustomMomentDateAdapter } from './helpers/adapter';
import { MaskitoDirective } from '@maskito/angular';

@Component({
  selector: 'soph-date-picker-form-field',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MaskitoDirective,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es' },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS },
    { provide: DateAdapter, useClass: CustomMomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  ],
  templateUrl: './date-picker-form-field.component.html',
  styleUrl: './date-picker-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerFormFieldComponent {
  @Input() control: FormControl = new FormControl();
  @Input() label!: string;
  @Input() errorMessages!: ErrorMessages;

  @Input() useMaxDate: boolean = false;
  @Input() maxDate?: Date;
  @Input() readonly: boolean = true;

  @Input() inputEvent?: (event: Event) => void = () => {};
  @Input() keydownEvent?: (event: KeyboardEvent) => void = () => {};
  @Input() type!: string;
  @Output() dateChange = new EventEmitter<Date>();

  protected readonly today: Date = new Date();
  protected readonly datePickerHeader = DatePickerHeader;
  protected readonly dateMask: MaskitoOptions = dateMask;

  get hasError() {
    return this.control?.touched && this.control?.invalid;
  }

  get errorMessage() {
    if (!this.control) return '';
    const errors = this.control.errors;
    if (errors) {
      for (const error of Object.keys(errors) as (keyof ErrorMessages)[]) {
        if (errors[error]) {
          return this.errorMessages[error] || '';
        }
      }
    }
    return '';
  }

  ngOnInit() {
    this.control.valueChanges.subscribe((value) => {
      this.dateChange.emit(value);
    });
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const limit = this.maxDate || this.today;
    return !this.useMaxDate || date <= limit;
  };
}
